import { useRecoilCallback, useRecoilValue, useSetRecoilState } from "recoil";
import { useEffect, useMemo } from "react";
import useEffectSkipFirst from "../../../hooks/useEffectSkipFirst";
import { weatherTimeWithID, weatherVisibleWithID } from "../../../store/WeatherContentsState";
import { addMapLegendSelector, removeMapLegendSelector } from "../../../store/MapLegendState";
import WxRangeAltDispatcher from "./WxRangeAltDispatcher";
import WxIndividualAltDispatcher from "./WxIndividualAltDispatcher";
import { elapsedTimeForWeatherState } from "../../../store/ElapsedTimeState";
import { isEmpty, parseUTCDate } from "../../../utils/FPEUtils";
import { useATCPlan } from "../../../store/ATCPlanState";

function WxContentsDispatcher({contentsID, contents, altType}) {
  const visible = useRecoilValue(weatherVisibleWithID(contentsID));
  const addMapLegend = useSetRecoilState(addMapLegendSelector);
  const removeMapLegend = useSetRecoilState(removeMapLegendSelector);
  const elapsedTimeForWeather = useRecoilValue(elapsedTimeForWeatherState);
  const {atcPlan} = useATCPlan();

  const visibleDependency = useMemo(() => ([visible]), [visible]);

  const changeContentsTime = useRecoilCallback(({set}) => timeInfo => {
    const contentsID = timeInfo.id;
    set(weatherTimeWithID(contentsID), timeInfo);
  });

  useEffect(() => {
    const dispatcher = (action) => {
      if (action.type === 'SET_CONTENTS_TIME') {
        changeContentsTime(action.payload);
      }
    }
    contents.setDispatcher(dispatcher);
    if(visible) {
      contents.drawLayer(visible);
      if (typeof contents.getLegend === 'function') {
        addMapLegend(contents.getLegend());
      }
    }
    // eslint-disable-next-line
  }, []);

  useEffectSkipFirst(async() => {
    await contents.drawLayer(visible);

    if (typeof contents.getLegend === 'function') {
      if (visible && contents.visible) {
        addMapLegend(contents.getLegend());
      } else {
        removeMapLegend(contents.getLegend());
      }
    }
  }, visibleDependency);

  useEffect(()=> {
    const json = atcPlan.edit.json;
    let etd = null;
    if(!isEmpty(json)) {
      if(json.dof) {
        etd = parseUTCDate(`${json.dof} ${json.dep.etd}00`);
      } else {
        etd = new Date();
        const depTime = parseInt(json.dep.etd.substr(0, 2));
        const depMin = parseInt(json.dep.etd.substr(2));
        if(depTime < etd.getUTCHours() || (depTime === etd.getUTCHours() && depMin < etd.getUTCMinutes())) {
          etd.setUTCDate(etd.getUTCDate() + 1);
        }
        etd.setUTCHours(depTime, depMin, 0);
      }
      contents.applyPlanETD && contents.applyPlanETD(etd);
    }
  }, [atcPlan.edit.json, contents]);

  useEffect(()=>{
    contents.applyElapsed && contents.applyElapsed(elapsedTimeForWeather);
  }, [elapsedTimeForWeather, contents])

  return (
    <>
      {altType === 'range' && <WxRangeAltDispatcher contents={contents}/>}
      {altType === 'individual' && <WxIndividualAltDispatcher contentsID={contentsID} contents={contents}/>}
    </>
  );
}

export default WxContentsDispatcher